import { useEffect, useRef, useState } from 'react';

interface UseInViewOptions {
  /** Fraction of the element that must be visible to trigger (0–1). */
  threshold?: number;
  /** Root margin, e.g. "0px 0px -10% 0px" to trigger slightly before entry. */
  rootMargin?: string;
  /** Fire once then stop observing (default true — reveals shouldn't replay). */
  once?: boolean;
}

/**
 * Attach the returned `ref` to any element; `inView` flips true when it scrolls
 * into the viewport. Self-cleaning and SPA-safe (re-observes when the node
 * remounts across route changes). If IntersectionObserver is unavailable, it
 * fails open — `inView` is true immediately so content is never hidden.
 */
export function useInView<T extends Element = HTMLDivElement>(
  options: UseInViewOptions = {},
): [React.RefObject<T>, boolean] {
  const { threshold = 0.15, rootMargin = '0px 0px -8% 0px', once = true } = options;
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true); // fail open — never leave content invisible
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            if (once) observer.unobserve(entry.target);
          } else if (!once) {
            setInView(false);
          }
        });
      },
      { threshold, rootMargin },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold, rootMargin, once]);

  return [ref, inView];
}

export default useInView;
